/**
 * The shell's icons, **taken from the #293 wireframes rather than drawn again**.
 *
 * Every path here is copied from the design canvas artboards (`Memory`, `Menus`, `Narrow`),
 * so the panel and the drawings agree by construction rather than by eye. They are a
 * consistent 24×24 stroke set — 1.8 stroke, round caps and joins — which is why they are one
 * module with one wrapper instead of an SVG pasted into each component.
 *
 * **`currentColor` everywhere.** The artboards hard-code `#46586b` and `#1e3a2e` because a
 * static mock has no state; here a nav item's icon has to follow its label through hover and
 * the selected pill, and the whole palette has to invert in dark mode. Inheriting the colour
 * is what makes both work with no per-state icon.
 *
 * `aria-hidden`, because each one sits beside a real label. An icon that announced itself
 * would double every nav item for a screen reader.
 */

import type { ReactNode } from "react";

function Icon({ size = 17, children }: { size?: number; children: ReactNode }) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** What Boone knows — a brain. */
export function MemoryIcon() {
  return (
    <Icon>
      <path d="M12 3a4 4 0 0 0-4 4v1a3 3 0 0 0 0 6v2a4 4 0 0 0 8 0v-2a3 3 0 0 0 0-6V7a4 4 0 0 0-4-4z" />
    </Icon>
  );
}

/** What's on your plate — a list. */
export function ListIcon() {
  return (
    <Icon>
      <line x1="8" y1="7" x2="20" y2="7" />
      <line x1="8" y1="12" x2="20" y2="12" />
      <line x1="8" y1="17" x2="20" y2="17" />
      <line x1="4" y1="7" x2="4.01" y2="7" />
      <line x1="4" y1="12" x2="4.01" y2="12" />
      <line x1="4" y1="17" x2="4.01" y2="17" />
    </Icon>
  );
}

/** Who's here — a person, with the plus that says others can be added. */
export function RosterIcon() {
  return (
    <Icon>
      <path d="M16 19v-1a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v1" />
      <circle cx="9.5" cy="7" r="3" />
      <path d="M17 11h4" />
      <path d="M19 9v4" />
    </Icon>
  );
}

/** What's changed — a clock. */
export function HistoryIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </Icon>
  );
}

/**
 * Your households — a roof over a door (#360).
 *
 * A house rather than the people in it: this page is about the *households* somebody belongs
 * to, where `RosterIcon` one section up is about who is in one of them.
 */
export function HouseholdsIcon() {
  return (
    <Icon size={18}>
      <path d="M3 10.5 12 4l9 6.5" />
      <path d="M5.5 9.8V20h13V9.8" />
      <path d="M10 20v-5h4v5" />
    </Icon>
  );
}

/** Connected apps — two stacked panes. */
export function AppsIcon() {
  return (
    <Icon size={18}>
      <rect x="3" y="4" width="18" height="7" rx="2" />
      <rect x="3" y="14" width="18" height="6" rx="2" />
      <line x1="7" y1="7.5" x2="7.01" y2="7.5" />
      <line x1="7" y1="17" x2="7.01" y2="17" />
    </Icon>
  );
}

/** Your account — a person. */
export function AccountIcon() {
  return (
    <Icon size={18}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </Icon>
  );
}

/** Sign out — a door with an arrow leaving it. */
export function SignOutIcon() {
  return (
    <Icon size={18}>
      <path d="M15 17l5-5-5-5" />
      <path d="M20 12H9" />
      <path d="M11 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5" />
    </Icon>
  );
}

/** The disclosure caret on a menu trigger. */
export function ChevronIcon() {
  return (
    <Icon size={13}>
      <polyline points="6 9 12 15 18 9" />
    </Icon>
  );
}

/** The current item in a menu. */
export function CheckIcon() {
  return (
    <Icon size={16}>
      <polyline points="20 6 9 17 4 12" />
    </Icon>
  );
}

/** Opens the drawer at phone widths. */
export function MenuIcon() {
  return (
    <Icon size={20}>
      <line x1="4" y1="7" x2="20" y2="7" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="17" x2="20" y2="17" />
    </Icon>
  );
}

/** Closes it again. */
export function CloseIcon() {
  return (
    <Icon size={20}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </Icon>
  );
}

/** Plan and billing — the card on `Roster.dc.html`'s plan row (#303). */
export function PlanIcon() {
  return (
    <Icon>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <line x1="3" y1="10.5" x2="21" y2="10.5" />
    </Icon>
  );
}

/**
 * A section that is open, or one that is folded away — `Memory.dc.html`'s section heads (#329).
 *
 * One component with a direction rather than two icons, because the two states are the same
 * mark rotated and the artboards draw them that way: a down chevron on an open section, a
 * right chevron on the folded one.
 */
export function DisclosureIcon({ open }: { open: boolean }) {
  return (
    <Icon size={15}>
      {open ? <polyline points="6 9 12 15 18 9" /> : <polyline points="9 6 15 12 9 18" />}
    </Icon>
  );
}

/** *Take a copy of everything* — an arrow onto a line (`History.dc.html`). */
export function DownloadIcon() {
  return (
    <Icon size={16}>
      <path d="M12 3v12" />
      <polyline points="7 11 12 16 17 11" />
      <path d="M4 20h16" />
    </Icon>
  );
}

/** The `+` on an *Add* control (`Memory.dc.html`). */
export function PlusIcon() {
  return (
    <Icon size={12}>
      <line x1="12" y1="6" x2="12" y2="18" />
      <line x1="6" y1="12" x2="18" y2="12" />
    </Icon>
  );
}
